import _extends from "@babel/runtime/helpers/extends";
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { m, mt, mr, mb, ml, mx, my, p, pt, pr, pb, pl, px, py } from '../Spacing/core';
import { gapTemplate, columnGapTemplate, rowGapTemplate, flexDirectionTemplate, flexWrapTemplate, justifyContentTemplate, alignItemsTemplate, flexGrowTemplate } from './core';

/** @import { ForwardRefRenderFunction } from 'react' */
/** @import { CoreSizes, ViewportProps } from '../Spacing/core' */
/** @import { AlignItemsValues, DirectionValues, Grow, JustifyContentValues, WrapValues } from './core' */

const FlexStyle = styled.div`
  display: ${({
  $inline
}) => $inline ? 'inline-flex' : 'flex'};
  ${({
  $justify
}) => justifyContentTemplate($justify)};
  ${({
  $align
}) => alignItemsTemplate($align)}
  ${({
  $wrap
}) => flexWrapTemplate($wrap)}
  ${({
  $direction
}) => flexDirectionTemplate($direction)}
  ${({
  $gap
}) => gapTemplate($gap)}
  ${({
  $rowGap
}) => rowGapTemplate($rowGap)}
  ${({
  $columnGap
}) => columnGapTemplate($columnGap)}
  ${({
  $grow
}) => flexGrowTemplate($grow)}
  ${({
  $m
}) => m($m)}
  ${({
  $mx
}) => mx($mx)}
  ${({
  $my
}) => my($my)}
  ${({
  $mt
}) => mt($mt)}
  ${({
  $mr
}) => mr($mr)}
  ${({
  $mb
}) => mb($mb)}
  ${({
  $ml
}) => ml($ml)}
  ${({
  $p
}) => p($p)}
  ${({
  $px
}) => px($px)}
  ${({
  $py
}) => py($py)}
  ${({
  $pt
}) => pt($pt)}
  ${({
  $pr
}) => pr($pr)}
  ${({
  $pb
}) => pb($pb)}
  ${({
  $pl
}) => pl($pl)}
`;

/**
 * @typedef {object} FlexProps Flex Component Properties
 * @prop {boolean} [inline] Render the component as an inline-flex container
 * @prop {ViewportProps|JustifyContentValues} [justify] Define the alignment of items along the main axis.
 * @prop {ViewportProps|AlignItemsValues} [align] Defines the behavior for how items are laid out along the cross axis on the current line.
 * @prop {ViewportProps|WrapValues} [wrap] Define how items should wrap in this flex container.
 * @prop {ViewportProps|DirectionValues} [direction] Define the direction for items placed in this flex container.
 * @prop {ViewportProps|CoreSizes} [gap] Define the space between items
 * @prop {ViewportProps|CoreSizes} [rowGap] Define the space between items in a row
 * @prop {ViewportProps|CoreSizes} [columnGap] Define the space between items in a column
 * @prop {ViewportProps|Grow} [grow] Define the flex-grow property for this flex container
 * @prop {ViewportProps|CoreSizes} [m] Margin on all sides
 * @prop {ViewportProps|CoreSizes} [mx] Left and Right margin
 * @prop {ViewportProps|CoreSizes} [my] Top and Bottom margin
 * @prop {ViewportProps|CoreSizes} [mt] Top margin
 * @prop {ViewportProps|CoreSizes} [mr] Right margin
 * @prop {ViewportProps|CoreSizes} [mb] Bottom margin
 * @prop {ViewportProps|CoreSizes} [ml] Left margin
 * @prop {ViewportProps|CoreSizes} [p] Padding on all sides
 * @prop {ViewportProps|CoreSizes} [px] Left and Right padding
 * @prop {ViewportProps|CoreSizes} [py] Top and Bottom padding
 * @prop {ViewportProps|CoreSizes} [pt] Top padding
 * @prop {ViewportProps|CoreSizes} [pr] Right padding
 * @prop {ViewportProps|CoreSizes} [pb] Bottom padding
 * @prop {ViewportProps|CoreSizes} [pl] Left padding
 */

/**
 * A container with configurable responsive margins
 *
 * @type {ForwardRefRenderFunction<HTMLDivElement, FlexProps>}
 */
const Flex = /*#__PURE__*/forwardRef(({
  inline,
  justify,
  align,
  wrap,
  direction,
  gap,
  rowGap,
  columnGap,
  grow,
  m: mProp,
  mx: mxProp,
  my: myProp,
  mt: mtProp,
  mr: mrProp,
  mb: mbProp,
  ml: mlProp,
  p: pProp,
  px: pxProp,
  py: pyProp,
  pt: ptProp,
  pr: prProp,
  pb: pbProp,
  pl: plProp,
  ...props
}, ref) => /*#__PURE__*/React.createElement(FlexStyle, _extends({
  ref: ref,
  $inline: inline,
  $justify: justify,
  $align: align,
  $wrap: wrap,
  $direction: direction,
  $gap: gap,
  $rowGap: rowGap,
  $columnGap: columnGap,
  $grow: grow,
  $m: mProp,
  $mx: mxProp,
  $my: myProp,
  $mt: mtProp,
  $mr: mrProp,
  $mb: mbProp,
  $ml: mlProp,
  $p: pProp,
  $px: pxProp,
  $py: pyProp,
  $pt: ptProp,
  $pr: prProp,
  $pb: pbProp,
  $pl: plProp
}, props)));
Flex.displayName = 'Flex';
export default Flex;
//# sourceMappingURL=index.js.map